import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ScrollService } from '../../services/scroll.service';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.css']
})
export class HeaderComponent implements OnInit, OnDestroy {
  activeSection = 'home';
  isMenuOpen = false;
  isTransitioning = false;
  currentRoute = '';
  private subscription: Subscription = new Subscription();

  navigationItems = [
    { id: 'home', label: 'Home', type: 'route', route: '/' },
    { id: 'about-page', label: 'About', type: 'static', href: '/about.html' },
    { id: 'services', label: 'Services', type: 'section' },
    { id: 'testimonials', label: 'Testimonials', type: 'section' }
  ];

  constructor(
    private scrollService: ScrollService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.currentRoute = this.router.url.split('?')[0] || '/';

    // Track current route
    this.subscription.add(
      this.router.events.pipe(
        filter(event => event instanceof NavigationEnd)
      ).subscribe((event: NavigationEnd) => {
        this.currentRoute = event.urlAfterRedirects;
      })
    );
    
    // Subscribe to active section changes
    this.subscription.add(
      this.scrollService.activeSection$.subscribe(section => {
        this.activeSection = section;
      })
    );
    
    // Initialize scroll listener after view is ready
    setTimeout(() => {
      this.initializeScrollDetection();
    }, 1000);
  }

  /**
   * Initialize scroll detection for active section highlighting
   */
  private initializeScrollDetection(): void {
    // Force initial active section detection
    const ionContent = document.querySelector('ion-content');
    if (ionContent) {
      ionContent.getScrollElement().then(scrollElement => {
        // Trigger initial scroll detection
        const event = new Event('scroll');
        scrollElement.dispatchEvent(event);
      });
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /**
   * Navigate to a specific section
   * @param sectionId - The ID of the section to navigate to
   */
  navigateToSection(sectionId: string): void {
    // If not on home page, navigate to home first
    if (this.currentRoute !== '/') {
      this.router.navigate(['/']).then(() => {
        setTimeout(() => {
          this.scrollService.scrollToElement(sectionId);
        }, 100);
      });
      return;
    }
    
    // Close mobile menu first
    this.isMenuOpen = false;
    
    // Small delay to ensure menu closes before scrolling
    setTimeout(() => {
      this.scrollService.scrollToElement(sectionId);
    }, 100);
  }

  /**
   * Handle click events for navigation
   */
  onNavClick(event: Event, item: { id: string; type: string; route?: string; href?: string }): void {
    event.preventDefault();
    event.stopPropagation();
    this.isMenuOpen = false;

    if (item.type === 'route' && item.route) {
      this.router.navigate([item.route]);
      return;
    }

    if (item.type === 'static' && item.href) {
      window.location.href = item.href;
      return;
    }

    this.navigateToSection(item.id);
  }

  isNavItemActive(item: { id: string; type: string; route?: string; href?: string }): boolean {
    if (item.type === 'route') {
      return item.route === '/' ? this.currentRoute === '/' : this.currentRoute.startsWith(item.route || '');
    }

    if (item.type === 'static') {
      return false;
    }

    return this.currentRoute === '/' && this.activeSection === item.id;
  }

  /**
   * Navigate to home page
   */
  public navigateToHome(): void {
    this.isMenuOpen = false;
    this.router.navigate(['/']);
  }

  /**
   * Toggle mobile menu
   */
  toggleMenu(): void {
    if (this.isMenuOpen) {
      // Closing menu
      this.isTransitioning = true;
      setTimeout(() => {
        this.isMenuOpen = false;
        this.isTransitioning = false;
      }, 300);
    } else {
      // Opening menu
      this.isMenuOpen = true;
      this.isTransitioning = true;
      setTimeout(() => {
        this.isTransitioning = false;
      }, 300);
    }
  }

  /**
   * Close mobile menu when clicking outside
   */
  closeMenu(): void {
    this.isMenuOpen = false;
  }

  /**
   * Navigate to intake form page
   */
  navigateToIntake(): void {
    this.isMenuOpen = false;
    this.router.navigate(['/scheduling']);
  }
}
